<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Spinner } from "@/shared/ui/spinner";

const { t } = useI18n();

const props = defineProps({
  links: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const visibleLinks = computed(() => (props.links || []).filter((l) => !!l));

function getTitle(link) {
  return (
    link?.info?.name ||
    link?.info?.code ||
    link?.template_name ||
    link?.template_code ||
    t("Доверенность")
  );
}

function getNotary(link) {
  return (
    link?.notary_full_name ||
    link?.notary?.full_name ||
    [
      link?.notary?.last_name,
      link?.notary?.first_name,
      link?.notary?.middle_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    null
  );
}

function getRegistryNumber(link) {
  return (
    link?.registry_number ||
    link?.info?.registry_number ||
    link?.target_transaction_number ||
    null
  );
}

function getBsoNumber(link) {
  return link?.bso_number || link?.info?.bso_number || null;
}

function getLicense(link) {
  return link?.license || link?.notary?.license_status || null;
}

function getStatus(link) {
  return link?.status || link?.info?.status || null;
}

function getDescription(link) {
  return link?.description || link?.info?.description || null;
}

function getParticipants(link) {
  if (!Array.isArray(link?.participants)) return "";
  return link.participants
    .map((p) => {
      if (p?.first_name) {
        return [p.first_name, p.last_name].filter(Boolean).join(" ");
      }
      return p?.params?.company_name || p?.identifier || "";
    })
    .filter(Boolean)
    .join(", ");
}
</script>

<template>
  <div v-if="loading" class="flex justify-center items-center py-4">
    <Spinner size="sm" />
  </div>

  <div v-else-if="visibleLinks.length" class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <svg-icon name="huge-check-mark" class="w-4 h-4 text-primary" />
      <div class="text-foreground text-sm font-semibold">
        {{ $t("Действует по доверенности") }}
      </div>
    </div>

    <div
      v-for="link in visibleLinks"
      :key="link.id || link.uuid || link.target_transaction_id"
      class="border border-solid border-border rounded-2xl p-4 bg-card flex flex-col gap-2"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="text-foreground text-sm font-semibold">
          {{ getTitle(link) }}
        </div>
        <div
          v-if="getStatus(link)"
          class="text-xs text-muted-foreground whitespace-nowrap"
        >
          {{ getStatus(link) }}
        </div>
      </div>

      <div
        v-if="getDescription(link)"
        class="text-sm text-muted-foreground whitespace-pre-line"
      >
        {{ getDescription(link) }}
      </div>

      <div v-else class="flex flex-col gap-1 text-xs text-muted-foreground">
        <div v-if="getNotary(link)">
          <span class="font-medium">{{ $t("Нотариус") }}:</span>
          {{ getNotary(link) }}
        </div>
        <div v-if="getLicense(link)">
          <span class="font-medium">{{ $t("Лицензия") }}:</span>
          {{ getLicense(link) }}
        </div>
        <div v-if="getRegistryNumber(link)">
          <span class="font-medium">{{ $t("Номер по реестру") }}:</span>
          {{ getRegistryNumber(link) }}
        </div>
        <div v-if="getBsoNumber(link)">
          <span class="font-medium">{{ $t("Номер бланка") }}:</span>
          {{ getBsoNumber(link) }}
        </div>
        <div v-if="getParticipants(link)">
          <span class="font-medium">{{ $t("Участники") }}:</span>
          {{ getParticipants(link) }}
        </div>
      </div>
    </div>
  </div>
</template>
